import Cropper from "cropperjs"

/*
 * CropManager
 *
 * Manages cropperjs instances, including creation, destruction, and
 * extraction of crop data. Handles the modal-based cropping workflow.
 *
 * Usage:
 *   const manager = new CropManager(errorHandler, imageProcessor)
 *   manager.startCropper(imageElement, { aspectRatio: 1, initialCrop: {...} })
 *   const data = manager.getCropData()
 *   manager.destroy()
 */
export class CropManager {
  /*
   * constructor
   *
   * Creates a new CropManager instance.
   *
   * Inputs:
   *   errorHandler   {ErrorHandler}   - Error handler for reporting errors.
   *   imageProcessor {ImageProcessor} - Image processor for calculations.
   */
  constructor(errorHandler, imageProcessor) {
    this.errorHandler = errorHandler
    this.imageProcessor = imageProcessor
    this.cropper = null
    this.activeType = null
  }

  /*
   * startCropper
   *
   * Initialises a cropperjs instance on the given image element.
   *
   * Inputs:
   *   imageElement {HTMLImageElement} - The image to attach cropperjs to.
   *   options      {object}           - Configuration options:
   *     aspectRatio  {number}         - Aspect ratio for crop box. NaN = free.
   *     initialCrop  {object|null}    - Initial crop data {x, y, width, height}.
   *     type         {string}         - Image type identifier for tracking.
   *
   * Output: none (side effect: creates this.cropper)
   */
  startCropper(imageElement, options = {}) {
    this.destroy()

    const { aspectRatio = NaN, initialCrop = null, type = null } = options
    this.activeType = type

    const naturalW = imageElement.naturalWidth || 0
    const naturalH = imageElement.naturalHeight || 0

    const hasValidCrop = initialCrop &&
      Number(initialCrop.width) > 0 &&
      Number(initialCrop.height) > 0

    const cropData = hasValidCrop
      ? {
          x: Number(initialCrop.x) || 0,
          y: Number(initialCrop.y) || 0,
          width: Number(initialCrop.width) || 0,
          height: Number(initialCrop.height) || 0
        }
      : this.imageProcessor.calculateDefaultCrop(naturalW, naturalH, aspectRatio)

    const cropperOptions = {
      viewMode: 1,
      autoCropArea: 1,
      checkCrossOrigin: false,
      checkOrientation: false,
      responsive: true,
      restore: true,
      guides: false,
      center: false,
      highlight: false,
      cropBoxMovable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: false,
      minCanvasWidth: 100,
      minCanvasHeight: 100,
      data: null,
      ready: () => {
        if (this.cropper) {
          this.cropper.setData(cropData)
        }
      }
    }

    if (!Number.isNaN(aspectRatio) && aspectRatio > 0) {
      cropperOptions.aspectRatio = aspectRatio
    }

    this.cropper = new Cropper(imageElement, cropperOptions)
  }

  /*
   * getCropData
   *
   * Gets the current crop data from the active cropperjs instance.
   *
   * Output:
   *   {object|null} - Crop data {x, y, width, height} with rounded values,
   *                   or null if no cropper is active.
   */
  getCropData() {
    if (!this.cropper) return null

    const data = this.cropper.getData(true)
    return {
      x: Math.round(data.x),
      y: Math.round(data.y),
      width: Math.round(data.width),
      height: Math.round(data.height)
    }
  }

  /*
   * getCroppedCanvas
   *
   * Gets the cropped canvas from the active cropperjs instance.
   *
   * Output:
   *   {HTMLCanvasElement|null} - The cropped canvas, or null if no cropper.
   */
  getCroppedCanvas() {
    if (!this.cropper) return null
    return this.cropper.getCroppedCanvas()
  }

  /*
   * isActive
   *
   * Checks if a cropper instance is currently active.
   *
   * Output:
   *   {boolean} - True if a cropper is active.
   */
  isActive() {
    return this.cropper !== null
  }

  /*
   * getActiveType
   *
   * Gets the type identifier of the currently active cropper.
   *
   * Output:
   *   {string|null} - The type identifier, or null if no cropper is active.
   */
  getActiveType() {
    return this.activeType
  }

  /*
   * destroy
   *
   * Destroys the current cropperjs instance if one exists.
   *
   * Output: none (side effect: destroys cropper, resets state)
   */
  destroy() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    this.activeType = null
  }
}
